import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import FilterModal from "../Filter Modal/FilterModal";
import axios from "axios";

export default function DataTable_Component({ apidata, columns, searchBy }) {
  const [dataFilter, setDataFilter] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [clientFilter, setClientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Holds the currently loaded API dataset.
  // This changes when a different month/year is selected.
  const [baseData, setBaseData] = useState(apidata || []);

  const [loading, setLoading] = useState(false);

  // Sync with the parent whenever its API data changes.
  useEffect(() => {
    setBaseData(apidata || []);
  }, [apidata]);

  // Fetch data for the selected month/year.
  const fetchFilteredData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const userRole = localStorage.getItem("userRole");

      if (!token || !userRole) {
        console.error("Token or user role not found");
        return;
      }

      let endpoint = "";

      if (userRole === "superadmin") {
        endpoint = "superadmin";
      } else if (userRole === "admin") {
        endpoint = "admin";
      } else if (userRole === "clientmanager") {
        endpoint = "clientmanager";
      } else {
        console.error("Invalid user role");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v4/salary-approvals/${endpoint}/salary-approvals`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // params: {
          //   ...(month ? { month } : {}),
          //   ...(year ? { year } : {}),
          // },
          params: {
            ...(fromDate && toDate
              ? { fromDate, toDate }
              : {
                  ...(month ? { month } : {}),
                  ...(year ? { year } : {}),
                }),
          },
        },
      );

      if (response.data.success) {
        const approvals = response.data.approvals || [];

        // Update the underlying dataset, not the derived filtered results.
        setBaseData(approvals);

        // Clear an existing client selection if that client
        // does not exist in the newly loaded dataset.
        if (
          clientFilter &&
          !approvals.some(
            (item) =>
              item.id_client?.name?.toLowerCase() ===
              clientFilter.toLowerCase(),
          )
        ) {
          setClientFilter("");
        }
      } else {
        setBaseData([]);
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search, client and status filters are applied to the
  // currently loaded dataset.
  const displayedData = useMemo(() => {
    let filtered = baseData || [];

    // Search by user name.
    if (dataFilter.trim()) {
      const searchValue = dataFilter.trim().toLowerCase();

      if (searchBy === "name") {
        filtered = filtered.filter((item) =>
          (item.id_user?.name || "").toLowerCase().startsWith(searchValue),
        );
      }
    }

    // Filter by client.
    if (clientFilter) {
      filtered = filtered.filter(
        (item) =>
          item.id_client?.name?.toLowerCase() === clientFilter.toLowerCase(),
      );
    }

    // Filter by approval status.
    if (statusFilter) {
      filtered = filtered.filter(
        (item) =>
          (item.status || "").toLowerCase() === statusFilter.toLowerCase(),
      );
    }

    return filtered;
  }, [baseData, dataFilter, searchBy, clientFilter, statusFilter]);

  // Total salary reflects all displayed matching records,
  // not just the current pagination page.
  const totalSalary = useMemo(() => {
    return displayedData.reduce(
      (sum, item) => sum + (Number(item.pay) || 0),
      0,
    );
  }, [displayedData]);

  const applyFilter = async () => {
    await fetchFilteredData();
    setShowFilterModal(false);
  };

  const removeFilter = () => {
    setClientFilter("");
    setStatusFilter("");
    setMonth("");
    setYear("");
    setFromDate("");
    setToDate("");
    setDataFilter("");

    // Restore the original dataset provided by the parent.
    setBaseData(apidata || []);

    setShowFilterModal(false);
  };

  return (
    <div className="table-responsive">
      <div className="row justify-content-between mb-2">
        <div className="col-md-3 mb-2">
          <input
            type="text"
            placeholder={`Search by ${searchBy}`}
            className="form-control"
            value={dataFilter}
            onChange={(e) => setDataFilter(e.target.value)}
          />
        </div>

        <div className="col-md-9 mb-2 d-flex justify-content-start align-items-center gap-3 flex-wrap">
          <button
            className="btn btn-primary"
            onClick={() => setShowFilterModal(true)}
          >
            Filter
          </button>

          <div className="btn btn-outline-secondary">
            Total Salary: ₹
            {Intl.NumberFormat("en-IN", {
              maximumFractionDigits: 2,
            }).format(totalSalary)}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading salary approvals...</div>
      ) : displayedData.length > 0 ? (
        <DataTable
          columns={columns}
          data={displayedData}
          pagination
          fixedHeader
          highlightOnHover
          responsive
          paginationPerPage={5}
          paginationRowsPerPageOptions={[5, 10, 15, 20]}
        />
      ) : (
        <div className="row justify-content-start">
          <div className="col-12">
            <p>No results found.</p>
          </div>
        </div>
      )}

      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        apidata={baseData}
        applyFilter={applyFilter}
        removeFilter={removeFilter}
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        month={month}
        setMonth={setMonth}
        year={year}
        setYear={setYear}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
      />
    </div>
  );
}
